import type { ReactNode } from "react";
import { MESSAGES } from "@dearbill/shared";

const LINK_CLASS = "text-primary-text hover:text-ember-dark hover:underline";

/** The customer app's English `disclaimer.banner`, the attribution notice the
 * Blue Button API Terms of Service require verbatim. It is read straight off the
 * shared message table, because a plain lookup needs no `I18nProvider`. */
const DISCLAIMER = MESSAGES.en["disclaimer.banner"];

/**
 * Footer on every member-portal screen: `PortalShell` mounts it under login,
 * OTP and the handoff, and `MemberLayout` under Home and the signing flow.
 *
 * It has the same three parts as the enrollment app's `DisclaimerFooter`: the
 * copyright, with the year computed at render; the CMS-compliance disclaimer;
 * and the legal links.
 *
 * It stays member-local rather than shared, because the two differ where it
 * counts:
 *   - the member SPA has no `I18nProvider`, and `useI18n()` throws without one,
 *     so the copy is English only: the disclaimer from `MESSAGES.en`, the rest
 *     literals;
 *   - the legal pages are routes of the customer SPA on another origin, so
 *     these are absolute `<a href>`s into `VITE_APP_BASE_URL`, not react-router
 *     `<Link>`s.
 */
export function PortalFooter(): ReactNode {
  const appBaseUrl = import.meta.env.VITE_APP_BASE_URL;
  return (
    <footer className="text-note text-slate-blue flex flex-col items-center gap-2 px-6 py-10 text-center sm:flex-row sm:gap-4">
      <p className="flex-1 sm:text-left">© {new Date().getFullYear()} DearBill, Inc.</p>
      <p className="flex-1">{DISCLAIMER}</p>
      <p className="flex-1 sm:text-right">
        <a href={`${appBaseUrl}/privacy-policy`} className={LINK_CLASS}>
          Privacy Policy
        </a>
        <span aria-hidden="true"> · </span>
        <a href={`${appBaseUrl}/terms-and-conditions`} className={LINK_CLASS}>
          Terms &amp; Conditions
        </a>
      </p>
    </footer>
  );
}
